import React from "react";
import { useTranslation } from "next-i18next";
import likeIcon from "../../../../../public/images/like-icon.svg";
import imgIcon from "../../../../../public/images/img-icon.svg";
import serviceIcon from "../../../../../public/images/service-icon.svg";
import Image from "next/image";
import { FilledDiamond } from "components/ui_components/FilledDiamond";
import { useGlobalState } from "context/GlobalStateContext";
import useIsDesktop from "app/custom_hooks/useIsDesktop";

// ✅ Composant SVG diamant rempli

const RatingDetails = ({ reviews }) => {
  const { t: translate } = useTranslation("page_detail");
  const { language } = useGlobalState();
  const globalReview = reviews?.avgGeneralReview;
  const clientService = reviews?.avgCustomerService;
  const productRating = reviews?.avgProductImageReview;
  const isDesktop = useIsDesktop();

  const getColorForRating = (rating) => {
    if (rating >= 0 && rating <= 2) return "#ef4444"; // rouge foncé
    if (rating > 2 && rating <= 3) return "#FF9430"; // orange
    if (rating > 3 && rating <= 4) return "#FFAE00"; // jaune-orangé
    if (rating > 4 && rating <= 5) return "#F2C335"; // jaune vif

    return "#d1d5db";
  };

  const renderDiamonds = (rating: number) => {
    const categoryColor = getColorForRating(rating); // Couleur unique pour toute la catégorie

    return [1, 2, 3, 4, 5].map((i) => {
      const filledPercentage = Math.min(1, Math.max(0, rating - (i - 1))) * 100;
      return (
        <div key={i} className={`${language === "ar" ? "-scale-x-100" : "scale-x-100"}`}>
          <FilledDiamond
            filledPercentage={filledPercentage}
            color={categoryColor} // Toujours la couleur de la catégorie
            emptyColor="#d1d5db"
          />
        </div>
      );
    });
  };
  const [windowWidth, setWindowWidth] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => setWindowWidth(window.innerWidth);
      setWindowWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);
  function displayRating(reviews) {
    const isArabic = language === "ar";
    if (isArabic) {
      // Arabic review strings
      if (reviews === 1) return translate("one_review");
      if (reviews === 2) return translate("two_reviews");

      const key = reviews <= 10 ? "reviews" : "review";
      return `${reviews} ${translate(key)}`;
    }

    // Other languages
    const key =
      reviews === 1
        ? "one_review"
        : reviews === 2
          ? "two_reviews"
          : reviews <= 10
            ? "reviews"
            : "review";

    return `${reviews} ${translate(key)}`;
  }

  return (
    <div
      className={`flex flex-col gap-2 ${
        windowWidth !== undefined && windowWidth >= 1300 && windowWidth < 1390
          ? "w-[16rem]"
          : "lg:w-[22rem]"
      }`}
    >
      {!isDesktop && (
        <p className="text-xl font-bold">
          {translate("rating_details")}( {displayRating(reviews?.totalReviews)})
        </p>
      )}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1 justify-between flex-wrap">
          <div className="flex items-center sm:gap-2 ">
            <div className="min-min">
              <Image
                src="/images/diamantv2.svg"
                alt="Like"
                width={8}
                height={10}
                className="h-5 w-5 object-cover"
              />
            </div>
            <div>
              <span className="xs:text-xs sm:text-sm font-semibold">
                {translate("global_review")}
              </span>
            </div>
          </div>
          <div className="flex ">
            {renderDiamonds(globalReview)}{" "}
            <span className="text-sm text-gray-500 ">
              {globalReview ? `(${globalReview})` : `(0.00)`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap- justify-between  xs:pt-[0.5rem] flex-wrap">
          <div className="flex items-center sm:gap-2 ">
            <div className="min-min">
              <Image
                src="/images/diamantv2.svg"
                alt="Like"
                width={8}
                height={10}
                className="h-5 w-5 object-cover "
              />
            </div>
            <span className="xs:text-xs sm:text-sm font-semibold">
              {translate("clients_service")}
            </span>
          </div>
          <div className="flex">
            {renderDiamonds(clientService)}{" "}
            <span className="text-sm text-gray-500">
              {clientService ? `(${clientService})` : `(0.00)`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-0 justify-between ">
          <div className="flex items-center ">
            <div className="min-w-min">
              <Image
                src="/images/diamantv2.svg"
                alt="Like"
                width={8}
                height={10}
                className="h-5 w-5 object-cover"
              />
            </div>
            <span className="xs:text-xs sm:text-sm font-semibold xs:ps-1 xs:pt-[0.3rem] whitespace-pre-line sm:pr-5">
              {translate("conformity_images")}
            </span>
          </div>
          <div className="flex ">
            {renderDiamonds(productRating)}{" "}
            <span className="text-sm text-gray-500 ">
              {productRating ? `(${productRating})` : `(0.00)`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingDetails;
